//
// Copyright (c) 2011-2025 by Curalate, Inc.
//

import React from 'react';
import styled, { useTheme } from 'styled-components';

import {
  GoogleTrackingParams,
  REFERRAL_PARAM_DISPLAY_VALUE,
  REFERRAL_PARAM_VALUE,
  RESERVED_PARAM_DISPLAY_VALUE,
} from '@account-settings/components/tracking-params/TrackingParamConstants';
import {
  TrackingParam,
  TrackingParamGroup,
} from '@apis/universal-image-actions/universalImageActionsApiModel';
import HR from '@components/atoms/hr/HR';
import { BodyBoldText } from '@components/atoms/text/Text';
import { DropdownSelect } from '@components/molecules/dropdown-select/DropdownSelect';
import { UiaProductTrackingIds } from '@uia/products/constants/UiaProductConstants';
import { TrackingParamInput } from '@uia/products/editor/curalate-product-editor/components/TrackingParamInput';

function getFormattedParamGroupHeader(paramGroupId: string) {
  switch (paramGroupId) {
    case 'google':
      return 'Google Analytics';
    case 'custom':
      return 'Custom Parameters';
    default:
      return paramGroupId;
  }
}

interface Props {
  paramGroup: TrackingParamGroup;
  onChangeParam: (param: TrackingParam) => void;
}

export const TrackingParamGroupEditor = ({
  onChangeParam,
  paramGroup,
}: Props) => {
  const theme = useTheme();

  const referralParam = paramGroup.params.find((p) => p.autoSet)?.name;
  const reservedParam = paramGroup.params.find((p) => p.curalateReserved);
  const isGoogleAnalyticsGroup = paramGroup.id === 'google';
  const params = isGoogleAnalyticsGroup
    ? GoogleTrackingParams
    : paramGroup.params;

  const dropdownOptions = [
    { label: 'None', value: 'none' },
    ...GoogleTrackingParams.filter((p) => p.name !== reservedParam?.name).map(
      (p) => ({ label: p.label, value: p.name }),
    ),
  ];

  const handleReferralParameterSelect = (value: string) => {
    const newReferralParamName = value === 'none' ? undefined : value;

    if (referralParam && referralParam !== newReferralParamName) {
      onChangeParam({
        name: referralParam,
        value: '',
        autoSet: false,
      });
    }

    if (newReferralParamName) {
      onChangeParam({
        name: newReferralParamName,
        value: REFERRAL_PARAM_VALUE,
        autoSet: true,
      });
    }
  };

  if (!params.length) {
    return null;
  }

  return (
    <GroupContainer>
      <h3>
        <ParamGroupHeader>
          {getFormattedParamGroupHeader(paramGroup.id)}
        </ParamGroupHeader>
      </h3>
      {isGoogleAnalyticsGroup && (
        <DropdownSelect
          dropdownContentZIndex={theme.zindex.popover}
          handleSelect={handleReferralParameterSelect}
          items={dropdownOptions}
          label='Referral Parameter'
          trackingId={
            UiaProductTrackingIds.PRODUCT_EDITOR_TRACKING_PARAMS_EDITOR_GOOGLE_REFERRAL_PARAM_DROPDOWN
          }
          value={referralParam ?? 'none'}
          width={352}
        />
      )}
      <HR />
      <ParamsContainer>
        {params.map((param) => {
          const paramGroupParam = paramGroup.params.find(
            (p) => p.name === param.name,
          );
          const isReserved = paramGroupParam?.curalateReserved ?? false;
          const isReferralParam = param.name === referralParam;
          const isReadOnly = !!(isReferralParam || isReserved);

          const displayValue = (() => {
            if (isReferralParam) {
              return REFERRAL_PARAM_DISPLAY_VALUE;
            }
            if (isReserved) {
              return RESERVED_PARAM_DISPLAY_VALUE;
            }
            return paramGroupParam?.value ?? '';
          })();

          const getOnChangeValue = (value: string) => {
            if (isReferralParam) {
              return REFERRAL_PARAM_VALUE;
            }
            if (isReserved) {
              return '';
            }
            return value;
          };

          return (
            <TrackingParamInput
              key={param.name}
              isReadOnly={isReadOnly}
              label={'label' in param ? param.label : param.name}
              onChange={(value) => {
                onChangeParam({
                  name: param.name,
                  value: getOnChangeValue(value),
                  autoSet: isReferralParam,
                });
              }}
              paramName={param.name}
              trackingId={
                UiaProductTrackingIds.PRODUCT_EDITOR_TRACKING_PARAMS_EDITOR_INPUT
              }
              value={displayValue}
            />
          );
        })}
      </ParamsContainer>
    </GroupContainer>
  );
};

const GroupContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[2]};
  padding-top: ${theme.spacings[3]};
`,
);

const ParamGroupHeader = styled(BodyBoldText)`
  text-transform: uppercase;
`;

const ParamsContainer = styled.div(
  ({ theme }) => `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacings[3]};
`,
);
